import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import AccessManagementPage from './pages/AccessManagementPage';
import UserManagementPage from './pages/UserManagementPage';
import JournalFinderPage from './pages/JournalFinderPage';
import EditorialesPage from './pages/EditorialesPage';
import RevistasPage from './pages/RevistasPage';
import ConcursosPage from './pages/ConcursosPage';
import ContestNavigatorPage from './pages/ContestNavigatorPage';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';
import ChatWindow from './components/ChatWindow';
import { useLanguage } from './hooks/useLanguage';
import './App.css';

const isMobileViewport = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 991.98px)').matches
);

function AppLayout() {
  const location = useLocation();
  const isLogin = location.pathname === '/login';
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobileViewport);
  const sidebarRef = useRef(null);
  const sidebarToggleRef = useRef(null);
  const toggleSidebar = useCallback(() => setSidebarCollapsed(c => !c), []);
  const closeSidebar = useCallback((restoreFocus = false) => {
    setSidebarCollapsed(true);
    if (restoreFocus && isMobile) {
      window.requestAnimationFrame(() => sidebarToggleRef.current?.focus());
    }
  }, [isMobile]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 991.98px)');
    const handleViewportChange = event => {
      setIsMobile(event.matches);
      if (event.matches) setSidebarCollapsed(true);
    };
    mediaQuery.addEventListener?.('change', handleViewportChange);
    return () => mediaQuery.removeEventListener?.('change', handleViewportChange);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('offcanvas-open', isMobile && !sidebarCollapsed);
    return () => document.body.classList.remove('offcanvas-open');
  }, [isMobile, sidebarCollapsed]);

  useEffect(() => {
    if (!isMobile || sidebarCollapsed) return undefined;

    const sidebar = sidebarRef.current;
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusable = () => Array.from(sidebar?.querySelectorAll(focusableSelector) || []);

    window.requestAnimationFrame(() => getFocusable()[0]?.focus());

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSidebar(true);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeSidebar, isMobile, sidebarCollapsed]);
  if (isLogin) {
    return (
      <Routes>
  <Route path="/login" element={<LoginPage />} />
      </Routes>
    );
  }
  return (
    <div className={`app-layout d-flex flex-column h-100`}>      
      <Navbar
        onToggleSidebar={toggleSidebar}
        sidebarCollapsed={sidebarCollapsed}
        toggleButtonRef={sidebarToggleRef}
      />
      <div className="app-body d-flex flex-grow-1 overflow-hidden">
        <Sidebar
          ref={sidebarRef}
          collapsed={sidebarCollapsed}
          mobileHidden={isMobile && sidebarCollapsed}
          onNavigate={() => isMobile && closeSidebar()}
        />
        {isMobile && !sidebarCollapsed && (
          <button
            type="button"
            className="app-sidebar-backdrop"
            onClick={() => closeSidebar(true)}
            aria-label={t('navbar.closeMenu')}
          />
        )}
        <main className="app-content flex-grow-1 overflow-auto p-4">
          <Routes>
            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/module/regulations" element={(<ProtectedRoute requireModuleId={1}><ChatWindow key="chat-regulations" title="Policy Navigator" apiEndpoint="/chat/regulations" /></ProtectedRoute>)} />
            <Route path="/module/journals" element={(<ProtectedRoute requireModuleId={2}><JournalFinderPage /></ProtectedRoute>)} />
            <Route path="/module/concursos" element={(<ProtectedRoute requireModuleId={7}><ContestNavigatorPage /></ProtectedRoute>)} />
            <Route path="/seguridad/accesos" element={(<ProtectedRoute requireModuleUrl="seguridad/accesos"><AccessManagementPage /></ProtectedRoute>)} />
            <Route path="/seguridad/usuarios" element={(<ProtectedRoute requireModuleUrl="seguridad/usuarios"><UserManagementPage /></ProtectedRoute>)} />
            <Route path="/mantenimiento/editoriales" element={(<ProtectedRoute requireModuleUrl="mantenimiento/editoriales"><EditorialesPage /></ProtectedRoute>)} />
            <Route path="/mantenimiento/revistas" element={(<ProtectedRoute requireModuleUrl="mantenimiento/revistas"><RevistasPage /></ProtectedRoute>)} />
            <Route path="/mantenimiento/concursos" element={(<ProtectedRoute requireModuleUrl="mantenimiento/concursos"><ConcursosPage /></ProtectedRoute>)} />
            <Route path="/login" element={<LoginPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </Router>
  );
}

export default App;
